import { getDriveEventsOptions, getVehicleLastStatusOptions } from '@/api/main'
import { Switch, useMantineTheme, useMantineColorScheme, MantineTheme } from '@mantine/core'
import { useSuspenseQueries } from '@tanstack/react-query'
import { milliseconds } from 'date-fns'
import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { last } from 'lodash-es'
import Map, { Source, Layer, Marker, Popup, MapRef, MapMouseEvent } from 'react-map-gl/mapbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import type { DriveDetectionModel, DriveSignPostModel } from '@/api/main/types.gen'
import type { GeographicCoordinates } from '@/api/main/types.gen'
import styles from './DriveDetailsMap.module.css'
import { Feature } from 'geojson'
import { MAPBOX_PUBLIC_TOKEN } from '@/constants'
import { useTranslation } from 'react-i18next'

const generateDetectionsGeoJSON = (detections: Array<DriveDetectionModel>, color: string) => {
  return {
    type: 'FeatureCollection' as const,
    features: detections.map((detection, index) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'LineString' as const,
        coordinates: [
          [detection.rear_center.longitude, detection.rear_center.latitude],
          [detection.front_center.longitude, detection.front_center.latitude],
        ],
      },
      properties: {
        index,
        color,
        plate: detection.plate || '',
      },
    })),
  }
}

const generateSignPostsGeoJSON = (signPosts: Array<DriveSignPostModel>) => {
  return {
    type: 'FeatureCollection' as const,
    features: signPosts.map((signPost, index) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [signPost.position.longitude, signPost.position.latitude],
      },
      properties: {
        index,
      },
    })),
  }
}

const VehiclePositionMarker = ({
  position,
  azimuth,
  theme,
}: {
  position: GeographicCoordinates
  azimuth?: number
  theme: MantineTheme
}) => {
  const centerX = 24
  const centerY = 24
  const arrowPoints = [
    [centerX, centerY],
    [centerX + 16, centerY + 14],
    [centerX, centerY - 18],
    [centerX - 16, centerY + 14],
    [centerX, centerY],
  ]
    .map(it => it.join(','))
    .join(' ')

  return (
    <Marker longitude={position.longitude} latitude={position.latitude} anchor="center">
      <svg width={48} height={48} style={{ overflow: 'visible' }}>
        <circle cx={centerX} cy={centerY} r={24} fill={theme.colors.red[6]} />
        <polygon
          points={arrowPoints}
          fill={theme.colors.gray[0]}
          transform={`rotate(${azimuth ?? 0} ${centerX} ${centerY})`}
        />
      </svg>
    </Marker>
  )
}

// Czech License Plate styled component
const CzechLicensePlate = ({ plate }: { plate: string }) => {
  return (
    <div className={styles.czechLicensePlate}>
      <div className={styles.czechPlateBlueStrip}></div>
      <div className={styles.czechPlateText}>{plate}</div>
    </div>
  )
}

const DriveDetailsMapGL = ({
  vehicleId,
  showPatrolVehiclePosition,
  driveId,
  isInactive,
}: {
  vehicleId: string
  showPatrolVehiclePosition: boolean
  driveId: string
  isInactive?: boolean
}) => {
  const { t } = useTranslation('driveDetails')
  const theme = useMantineTheme()
  const { colorScheme } = useMantineColorScheme()
  const [zoom, setZoom] = useState(18)
  const [userDefinedMapCenter, setUserDefinedMapCenter] = useState<GeographicCoordinates | null>(null)
  const [autoUpdateMapPosition, setAutoUpdateMapPosition] = useState(!isInactive)
  const mapRef = useRef<MapRef>(null)

  // Popup state - support multiple plates
  const [popupInfo, setPopupInfo] = useState<{ longitude: number; latitude: number; plates: string[] } | null>(null)

  const [
    { data: driveEvents },
    {
      data: { status: lastStatus },
    },
  ] = useSuspenseQueries({
    queries: [
      {
        ...getDriveEventsOptions({ path: { drive_id: Number(driveId) } }),
        refetchInterval: milliseconds({ seconds: 1 }),
      },
      {
        ...getVehicleLastStatusOptions({ path: { vehicle_id: vehicleId } }),
        refetchInterval: milliseconds({ seconds: 1 }),
      },
    ],
  })

  // Calculate auto-update center
  const autoUpdateMapCenter = useMemo(() => {
    if (showPatrolVehiclePosition && lastStatus) {
      return lastStatus.position
    }
    if (driveEvents.detections.length > 0) {
      return last(driveEvents.detections)!.rear_center
    }
    return null
  }, [showPatrolVehiclePosition, lastStatus, driveEvents.detections])

  const mapCenter = userDefinedMapCenter ?? autoUpdateMapCenter

  const detectionsGeoJSON = useMemo(
    () => generateDetectionsGeoJSON(driveEvents.detections, theme.colors.grape[9]),
    [driveEvents.detections, theme.colors.grape]
  )

  const signPostsGeoJSON = useMemo(() => generateSignPostsGeoJSON(driveEvents.sign_posts), [driveEvents.sign_posts])

  // View state for Mapbox
  const [viewState, setViewState] = useState(() => ({
    longitude: mapCenter?.longitude ?? 14.41204,
    latitude: mapCenter?.latitude ?? 50.01079,
    zoom: zoom,
  }))

  // Update view state when center or zoom changes
  useEffect(() => {
    if (mapCenter) {
      if (autoUpdateMapPosition && !isInactive) {
        setViewState(prev => ({
          ...prev,
          longitude: mapCenter.longitude,
          latitude: mapCenter.latitude,
        }))
      }
    }
  }, [mapCenter, autoUpdateMapPosition, isInactive])

  useEffect(() => {
    setViewState(prev => ({
      ...prev,
      zoom: zoom,
    }))
  }, [zoom])

  // Handle mouse move on the map
  const onHover = useCallback((event: MapMouseEvent) => {
    const { lngLat, point, features } = event

    if (!mapRef.current) {
      setPopupInfo(null)
      return
    }

    let allFeatures: Feature[] = []

    try {
      // Query ALL features at this exact pixel point
      allFeatures = mapRef.current.queryRenderedFeatures(point, {
        layers: ['detection-lines'],
      })
    } catch {
      allFeatures = features || []
    }

    if (!allFeatures || allFeatures.length === 0) {
      setPopupInfo(null)
      return
    }

    // Get unique plates from all features, sorted by index descending (most recent first)
    const featuresWithPlates = allFeatures
      .filter(f => f.properties?.plate)
      .sort((a, b) => (b.properties?.index ?? 0) - (a.properties?.index ?? 0))

    const uniquePlates = Array.from(new Set(featuresWithPlates.map(f => f.properties?.plate)))

    if (uniquePlates.length > 0) {
      setPopupInfo({
        longitude: lngLat.lng,
        latitude: lngLat.lat,
        plates: uniquePlates,
      })
    } else {
      setPopupInfo(null)
    }
  }, [])

  if (driveEvents.detections.length === 0 && (!showPatrolVehiclePosition || !lastStatus)) {
    return null
  }

  return (
    <>
      <Switch
        checked={autoUpdateMapPosition}
        disabled={isInactive}
        onChange={event => {
          setAutoUpdateMapPosition(event.currentTarget.checked)
          setUserDefinedMapCenter(event.currentTarget.checked ? null : autoUpdateMapCenter)
        }}
        label={t('followPatrolVehicle')}
        mb="md"
      />
      <div style={{ position: 'relative' }}>
        <Map
          ref={mapRef}
          id="driveDetailsMap"
          {...viewState}
          onMove={evt => {
            setViewState(evt.viewState)
            setZoom(evt.viewState.zoom)
            if (!autoUpdateMapPosition) {
              setUserDefinedMapCenter({
                latitude: evt.viewState.latitude,
                longitude: evt.viewState.longitude,
              })
            }
          }}
          onMouseMove={onHover}
          interactiveLayerIds={['detection-lines']}
          dragPan={isInactive || !autoUpdateMapPosition}
          style={{
            width: '100%',
            height: '50em',
            borderRadius: 'var(--mantine-radius-md)',
            marginBottom: 'var(--mantine-spacing-md)',
          }}
          mapStyle={`mapbox://styles/mapbox/${colorScheme === 'light' || colorScheme === 'auto' ? 'streets-v12' : 'dark-v11'}`}
          mapboxAccessToken={MAPBOX_PUBLIC_TOKEN}
        >
          {/* Vehicle detection lines */}
          <Source id="detections" type="geojson" data={detectionsGeoJSON}>
            <Layer
              id="detection-lines"
              type="line"
              paint={{
                'line-color': theme.colors.blue[9],
                'line-width': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  10, // At zoom level 10
                  3, // Line width is 3px
                  12, // At zoom level 12
                  3, // Line width is 3px
                  14, // At zoom level 14
                  3.5, // Line width is 3.5px
                  16, // At zoom level 16
                  4, // Line width is 4px
                  18, // At zoom level 18
                  5, // Line width is 5px
                  20, // At zoom level 20
                  6.5, // Line width is 6.5px
                  22, // At zoom level 22
                  8, // Line width is 8px
                  24, // At zoom level 24
                  10, // Line width is 10px
                ],
              }}
            />
          </Source>

          {/* Sign posts */}
          <Source id="signPosts" type="geojson" data={signPostsGeoJSON}>
            <Layer
              id="sign-post-circles"
              type="circle"
              paint={{
                'circle-radius': 5,
                'circle-color': 'white',
                'circle-stroke-width': 1,
                'circle-stroke-color': theme.colors.blue[9],
              }}
            />
          </Source>

          {/* Vehicle position marker */}
          {lastStatus && showPatrolVehiclePosition && (
            <VehiclePositionMarker position={lastStatus.position} azimuth={lastStatus.azimuth} theme={theme} />
          )}

          {/* Popup for plate tooltip */}
          {popupInfo && (
            <Popup
              longitude={popupInfo.longitude}
              latitude={popupInfo.latitude}
              closeButton={false}
              closeOnClick={false}
              anchor="top"
              className={styles.czechLicensePlatePopup}
            >
              {popupInfo.plates.map((plate, idx) => (
                <CzechLicensePlate key={idx} plate={plate} />
              ))}
            </Popup>
          )}
        </Map>
      </div>
    </>
  )
}

export default DriveDetailsMapGL
